import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1dfad621-fc79-4cd5-b25a-5a11bf07839e/artifacts/nz9707fn_fulllogo_transparent_nobuffer.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { 
    name: "Services", 
    path: "/services",
    dropdown: [
      { name: "Fiber Network Construction", path: "/services#fiber" },
      { name: "Coax/HFC Network Construction", path: "/services#coax" },
      { name: "Fiber Splicing & Testing", path: "/services#splicing" },
    ]
  },
  { name: "Capabilities", path: "/capabilities" },
  { name: "Safety", path: "/safety" },
  { name: "Projects", path: "/projects" },
  { name: "Careers", path: "/careers" },
  { name: "Vendor", path: "/vendor" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img
              src={LOGO_URL}
              alt="C&F Communications"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.dropdown ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger 
                    className={`flex items-center px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${
                      isActive(link.path) 
                        ? "text-[#00B4D8]" 
                        : "text-slate-700 hover:text-[#00B4D8]"
                    }`}
                    data-testid={`nav-${link.name.toLowerCase()}-dropdown`}
                  >
                    {link.name}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56 rounded-none border-slate-200">
                    {link.dropdown.map((item) => (
                      <DropdownMenuItem key={item.name} asChild>
                        <Link 
                          to={item.path} 
                          className="w-full cursor-pointer"
                          data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${
                    isActive(link.path) 
                      ? "text-[#00B4D8]" 
                      : "text-slate-700 hover:text-[#00B4D8]"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/contact"
              className="bg-[#00B4D8] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,1)]"
              data-testid="header-cta-button"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <div key={link.name}>
                <Link
                  to={link.path}
                  className={`block px-4 py-3 text-sm font-medium uppercase tracking-wide ${
                    isActive(link.path) 
                      ? "text-[#00B4D8] bg-slate-50" 
                      : "text-slate-700"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
                {link.dropdown && (
                  <div className="pl-4 space-y-1">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block px-4 py-2 text-xs font-medium text-slate-500 hover:text-[#00B4D8]"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4">
              <Link
                to="/contact"
                className="block text-center bg-[#00B4D8] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-cta"
              >
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
